import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import roadieBlocks from 'images/committed-capacity/roadie-blocks-row.svg'

import nideJob from 'images/committed-capacity/nice-job-row.svg'
import whatiSRoadieBlocks from 'images/committed-capacity/what-is-roadie-blocks-2.jpg'
import howToSignUpA from 'images/committed-capacity/how-signup-3a.jpg'
import howToSignUpB from 'images/committed-capacity/how-signup-3b.jpg'
import whatToKnow from 'images/committed-capacity/what-to-know-4.jpg'
import stackedMoney from 'images/committed-capacity/stacked-money.svg'
import { Container as Framework, Module, Slide } from 'components/pages/certifications/framework'
import Oops from 'components/pages/certifications/Oops'

import { Button, Container, Spacer, Text, Loader } from 'roadie-ui'
import { TRAINING_CERTS } from 'util/constants'
import { FeatureFlag } from 'Optimizely'
import { fetchDriverCerts } from 'actions/profile'
import { useDispatch } from 'hooks/useDispatch'
import { sendNativeMessage } from 'util/native'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

const ImgContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const CommittedCapacityPage = () => {
  const { t } = useLocalization()
  const [loading, setLoading] = React.useState(true)
  const dispatch = useDispatch()
  const { isWebview, trainingCertifications } = useSelector<
    any,
    {
      isWebview: boolean
      trainingCertifications: {
        name: string
      }[]
    }
  >((state) => ({
    isWebview: state.config.isWebview,
    trainingCertifications: state.trainingCertifications?.delivery_certifications_drivers ?? [],
  }))

  useEffect(() => {
    if (loading) {
      fetchDriverCerts()(dispatch).then(() => {
        setLoading(false)
      })
    }
  }, [dispatch, loading])

  const driverHasCert = trainingCertifications.find((cert) => cert.name === 'committed_capacity_eligible')

  return (
    <Container.Base $color="neutral-black">
      <Framework title={t('dweb_common_roadie_blocks')}>
        {loading ? (
          <Loader />
        ) : (
          <FeatureFlag feature="drive-roadie-blocks-module-requires-cert">
            {(moduleRequiresCert: boolean) =>
              (moduleRequiresCert && driverHasCert) || !moduleRequiresCert ? (
                <Module certification={TRAINING_CERTS.COMMITTED_CAPACITY}>
                  <Slide>
                    <ImgContainer>
                      <img src={roadieBlocks} alt={t('dweb_common_roadie_blocks')} />
                    </ImgContainer>
                    <Spacer $height="4rem" />
                    <Text.Overline $color="primary-base">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_what_need_know" />
                    </Text.Overline>
                    <Spacer $height=".4rem" />
                    <Text.H4>
                      <LocalizedText translationKey="dweb_common_roadie_blocks" />
                    </Text.H4>
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_elevate_earnings_roadie_blocks" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                  </Slide>
                  <Slide>
                    <Text.H5 $fontWeight="600">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_what_roadie_blocks" />
                    </Text.H5>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedRichText
                        translationKey="dweb_certifications_blocks_intro_with_highlight"
                        params={{
                          strongText1: (
                            <strong>
                              <LocalizedText translationKey="dweb_common_roadie_blocks" />
                            </strong>
                          ),
                        }}
                      />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <ImgContainer>
                      <img
                        src={whatiSRoadieBlocks}
                        alt={t('dweb_certifications_committed_capacity_alt_what_is_roadie_blocks')}
                      />
                    </ImgContainer>
                    <Spacer $height="4rem" />
                    <Text.Subtitle1>
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_whats_earnings_guarantee" />
                    </Text.Subtitle1>
                    <Spacer $height="1.6rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_guarantee_shown_upfront" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_adjustment_below_guarantee" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                  </Slide>
                  <Slide>
                    <Text.H5 $fontWeight="600">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_how_do_i_sign_up_blocks" />
                    </Text.H5>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_zone_signup_required" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_scheduler_access" />
                    </Text.Body2>
                    <Spacer $height="3.6rem" />
                    <ImgContainer>
                      <img src={howToSignUpA} alt={t('dweb_certifications_committed_capacity_alt_how_to_sign_up')} />
                    </ImgContainer>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_if_see_block_like_can_sign_up_block" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <ImgContainer>
                      <img src={howToSignUpB} alt={t('dweb_certifications_committed_capacity_alt_how_to_sign_up')} />
                    </ImgContainer>
                    <Spacer $height="2.4rem" />
                    <Text.Subtitle1 $fontWeight="500">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_joining_waitlist" />
                    </Text.Subtitle1>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_waitlist_intro" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_waitlist_auto_add" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                  </Slide>
                  <Slide>
                    <Text.H5 $fontWeight="600">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_driving_first_block" />
                    </Text.H5>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_start_navigation_prompt" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_start_in_zone" />
                    </Text.Body2>
                    <Spacer $height="2.4rem" />
                    <ImgContainer>
                      <img
                        src={whatToKnow}
                        alt={t('dweb_certifications_committed_capacity_alt_what_to_know_first_block')}
                      />
                    </ImgContainer>
                    <Spacer $height="2.4rem" />
                    <Text.Subtitle1 $fontWeight="500">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_getting_gigs" />
                    </Text.Subtitle1>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_route_after_start" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_once_first_gig_appears_within_app_youll_able" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                  </Slide>
                  <Slide>
                    <Text.H5 $fontWeight="600">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_getting_paid" />
                    </Text.H5>
                    <Spacer $height=".8rem" />
                    <Text.Subtitle1 $fontWeight="500">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_breaking_down_earnings_guarantees" />
                    </Text.Subtitle1>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_guarantee_exchange" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedRichText
                        translationKey="dweb_certifications_blocks_guarantee_varies"
                        params={{
                          strongText1: (
                            <strong>
                              <LocalizedText translationKey="dweb_certifications_committed_capacity_less_than" />
                            </strong>
                          ),
                        }}
                      />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedRichText
                        translationKey="dweb_certifications_blocks_no_adjustment_if_pay_exceeds_guarantee"
                        params={{
                          strongText1: (
                            <strong>
                              <LocalizedText translationKey="dweb_certifications_committed_capacity_more_than" />
                            </strong>
                          ),
                        }}
                      />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_guarantee_excludes_tips" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Container.Grid $gridTemplateColumns="50%" $justifyContent="center">
                      <ImgContainer>
                        <img src={stackedMoney} alt={t('dweb_certifications_committed_capacity_alt_stacked_money')} />
                      </ImgContainer>
                    </Container.Grid>
                    <Spacer $height="2.4rem" />
                    <Text.Subtitle1 $fontWeight="500">
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_how_do_i_get_paid" />
                    </Text.Subtitle1>
                    <Spacer $height=".8rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_instant_pay_access" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_weekly_payout_remainder" />
                    </Text.Body2>
                    <Spacer $height="2.2rem" />
                  </Slide>
                  <Slide>
                    <ImgContainer>
                      <img src={nideJob} alt={t('dweb_certifications_committed_capacity_alt_nice_job')} />
                    </ImgContainer>
                    <Spacer $height="4rem" />
                    <Text.H4>
                      <LocalizedText translationKey="dweb_certifications_committed_capacity_nice_job" />
                    </Text.H4>
                    <Text.Body2>
                      <LocalizedText translationKey="dweb_certifications_blocks_joined_next_steps" />
                    </Text.Body2>
                    {isWebview ? (
                      <Container.Position $position="absolute" $width="100%" $bottom="0">
                        <Button.Primary
                          type="button"
                          $width="100%"
                          onClick={() => sendNativeMessage({ type: 'FIND_ZONES' })}
                        >
                          <LocalizedText translationKey="dweb_certifications_committed_capacity_sign_up_zone" />
                        </Button.Primary>
                      </Container.Position>
                    ) : null}
                  </Slide>
                </Module>
              ) : (
                <Oops
                  message={t('dweb_certifications_blocks_not_eligible_message')}
                  url={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Roadie-Blocks-Frequently-Asked-Questions`}
                  urlTextKey="dweb_certifications_committed_capacity_learn_more_about_blocks"
                />
              )
            }
          </FeatureFlag>
        )}
      </Framework>
    </Container.Base>
  )
}

export default CommittedCapacityPage
